/**
 * There is no user account system by design: one shared token per installation, sent as X-Admin-Token.
 * Kept in localStorage so a refresh does not ask again, with an in-memory fallback for browsers that
 * refuse storage (private mode, blocked site data) so at least the session keeps working.
 */
const KEY = 'recramap.adminToken'

export type ClearReason = 'logout' | 'unauthorized'

let memory: string | null = null
const listeners = new Set<(reason: ClearReason) => void>()

export function readToken(): string | null {
  if (memory !== null) return memory
  try {
    return window.localStorage.getItem(KEY)
  } catch {
    return null
  }
}

export function writeToken(token: string): void {
  memory = token
  try {
    window.localStorage.setItem(KEY, token)
  } catch {
    // Storage unavailable; the in-memory copy carries this tab.
  }
}

export function clearToken(reason: ClearReason): void {
  memory = null
  try {
    window.localStorage.removeItem(KEY)
  } catch {
    // Nothing to clean up if storage was never available.
  }
  for (const listener of listeners) listener(reason)
}

/** Lets the app react when a 401 throws the token away mid-session. */
export function onTokenCleared(listener: (reason: ClearReason) => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
